<template>
  <div class="max-w-6xl mx-auto">
    <!-- Page Header -->
    <div class="text-center mb-8">
      <h1 class="text-3xl font-bold mb-4">Join a Competition</h1>
      <p class="text-base-content/70 max-w-2xl mx-auto">
        Enter a competition ID to join directly, or browse available competitions to find one that interests you.
      </p>
    </div>

    <!-- Quick Join Section -->
    <div class="card bg-base-100 card-bordered mb-8">
      <div class="card-body">
        <h2 class="card-title text-xl mb-4">
          <i class="mdi mdi-link text-primary"></i>
          Quick Join
        </h2>
        <p class="text-base-content/70 mb-4">
          Have a competition link? Enter the competition ID to join directly.
        </p>
        
        <form @submit.prevent="handleQuickJoin" class="flex gap-4">
          <div class="form-control flex-1">
            <input 
              v-model="quickJoinId"
              type="text" 
              placeholder="Enter competition ID (e.g., abc123)"
              class="input input-bordered" 
              required
            />
          </div>
          <button 
            type="submit" 
            class="btn btn-primary"
            :disabled="quickJoinLoading"
          >
            <span v-if="quickJoinLoading" class="loading loading-spinner loading-sm"></span>
            {{ quickJoinLoading ? 'Joining...' : 'Join' }}
          </button>
        </form>

        <!-- Quick Join Error -->
        <div v-if="quickJoinError" class="alert alert-error mt-4">
          <i class="mdi mdi-alert-circle h-6 w-6"></i>
          <span>{{ quickJoinError }}</span>
        </div>
      </div>
    </div>

    <!-- Available Competitions Section -->
    <div class="card bg-base-100 card-bordered">
      <div class="card-body">
        <div class="flex justify-between items-center mb-6">
          <h2 class="card-title text-xl">
            <i class="mdi mdi-trophy text-primary"></i>
            Available Competitions
          </h2>
          <button 
            @click="refreshCompetitions"
            class="btn btn-ghost btn-sm"
            :disabled="loading"
          >
            <i class="mdi mdi-refresh"></i>
            Refresh
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <span class="loading loading-spinner loading-lg"></span>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-error">
          <i class="mdi mdi-alert-circle h-6 w-6"></i>
          <span>{{ error }}</span>
        </div>

        <!-- No Competitions -->
        <div v-else-if="availableCompetitions.length === 0" class="text-center py-12">
          <div class="max-w-md mx-auto">
            <div class="text-6xl mb-4">🏆</div>
            <h4 class="text-lg font-semibold mb-2">No competitions available</h4>
            <p class="text-base-content/70 mb-6">
              There are no competitions open for joining at the moment.
            </p>
            <router-link to="/create" class="btn btn-primary">
              Create a Competition
            </router-link>
          </div>
        </div>

        <!-- Competitions Grid -->
        <div v-else class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CompetitionCard 
            v-for="competition in availableCompetitions" 
            :key="competition.id" 
            :competition="competition"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCompetitionStore } from '../stores/competition'
import CompetitionCard from '../components/CompetitionCard.vue'

const router = useRouter()
const competitionStore = useCompetitionStore()

// Quick join state
const quickJoinId = ref('')
const quickJoinLoading = ref(false)
const quickJoinError = ref('')

// Available competitions state
const loading = ref(false)
const error = ref('')
const availableCompetitions = ref([])

const handleQuickJoin = async () => {
  if (!quickJoinId.value.trim()) return
  
  quickJoinLoading.value = true
  quickJoinError.value = ''
  
  try {
    // Validate the competition exists
    const competition = await competitionStore.fetchCompetition(quickJoinId.value.trim())
    
    // Redirect to join form with the competition ID
    router.push(`/join-form?id=${competition.id}`)
  } catch (err) {
    quickJoinError.value = err.message || 'Competition not found. Please check the ID and try again.'
  } finally {
    quickJoinLoading.value = false
  }
}

const fetchAvailableCompetitions = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const competitions = await competitionStore.fetchCompetitions()
    // Filter to only show competitions that haven't started yet
    // Add null check to prevent filter error
    if (competitions && Array.isArray(competitions)) {
      availableCompetitions.value = competitions.filter(comp => !comp.is_locked)
    } else {
      availableCompetitions.value = []
      console.warn('fetchCompetitions returned unexpected data:', competitions)
    }
  } catch (err) {
    error.value = err.message || 'Failed to load competitions'
    availableCompetitions.value = []
    
    // Fallback: try to use competitions from store state
    if (competitionStore.competitions && Array.isArray(competitionStore.competitions)) {
      availableCompetitions.value = competitionStore.competitions.filter(comp => !comp.is_locked)
      error.value = '' // Clear error if fallback works
    }
  } finally {
    loading.value = false
  }
}

const refreshCompetitions = () => {
  fetchAvailableCompetitions()
}

onMounted(() => {
  fetchAvailableCompetitions()
})
</script> 